import React, { Component, type ReactNode } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { type ErrorBoundaryState, type AppError, ErrorType, ErrorSeverity } from '../../types/error';
import { errorService } from '../../services/errorService';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: AppError, resetError: () => void) => ReactNode;
  onError?: (error: AppError, errorInfo: React.ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number | boolean | null | undefined>;
}

interface ErrorBoundaryComponentState extends ErrorBoundaryState {
  showDetails: boolean;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryComponentState> {
  private resetTimeoutId?: number;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryComponentState> {
    // Convert the error to AppError format
    const appError = errorService.handleError(error, {
      severity: ErrorSeverity.CRITICAL,
      context: { source: 'ErrorBoundary' },
      userMessage: 'The application encountered an unexpected error',
    });

    return {
      hasError: true,
      error: appError,
      errorId: appError.id,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log additional error info
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    // Handle the error with additional context
    const appError = errorService.handleError(error, {
      severity: ErrorSeverity.CRITICAL,
      context: {
        source: 'ErrorBoundary',
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      },
      userMessage: 'The application encountered an unexpected error',
    });

    // Call the onError prop if provided
    if (this.props.onError) {
      this.props.onError(appError, errorInfo);
    }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetOnPropsChange, resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state if resetKeys have changed
    if (hasError && resetOnPropsChange && resetKeys) {
      const prevResetKeys = prevProps.resetKeys || [];
      const hasResetKeyChanged = resetKeys.some((key, idx) => key !== prevResetKeys[idx]);

      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  componentWillUnmount(): void {
    if (this.resetTimeoutId) {
      window.clearTimeout(this.resetTimeoutId);
    }
  }

  resetErrorBoundary = (): void => {
    if (this.resetTimeoutId) {
      window.clearTimeout(this.resetTimeoutId);
    }

    this.setState({
      hasError: false,
      error: null,
      errorId: undefined,
      showDetails: false,
      errorInfo: undefined,
    });
  };

  handleRetry = (): void => {
    this.resetErrorBoundary();
  };

  handleReload = (): void => {
    window.location.reload();
  };

  toggleDetails = (): void => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render(): ReactNode {
    const { hasError, error, showDetails, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, this.resetErrorBoundary);
      }

      // Default error UI
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          p={3}
        >
          <Card sx={{ maxWidth: 600, width: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ErrorIcon color="error" sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h5" component="h1">
                  Something went wrong
                </Typography>
              </Box>

              <Alert
                severity={this.getSeverityLevel(error.severity)}
                sx={{ mb: 2 }}
              >
                <Typography variant="body1" gutterBottom>
                  {error.userMessage || error.message}
                </Typography>
                {error.type !== ErrorType.UNKNOWN && (
                  <Typography variant="caption" display="block">
                    Error Type: {error.type.replace('_', ' ').toUpperCase()}
                  </Typography>
                )}
              </Alert>

              <Typography variant="body2" color="text.secondary" paragraph>
                We apologize for the inconvenience. You can try refreshing the page or
                contact support if the problem persists.
              </Typography>

              {process.env.NODE_ENV === 'development' && (
                <>
                  <Box display="flex" alignItems="center" mt={2}>
                    <IconButton
                      size="small"
                      onClick={this.toggleDetails}
                      aria-label={showDetails ? 'Hide details' : 'Show details'}
                    >
                      {showDetails ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">
                      {showDetails ? 'Hide' : 'Show'} technical details
                    </Typography>
                  </Box>

                  <Collapse in={showDetails}>
                    <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                      <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                        <strong>Error ID:</strong> {error.id}
                        {'\n'}
                        <strong>Message:</strong> {error.message}
                        {'\n'}
                        <strong>Type:</strong> {error.type}
                        {'\n'}
                        <strong>Severity:</strong> {error.severity}
                        {'\n'}
                        <strong>Timestamp:</strong> {error.timestamp.toISOString()}
                        {error.context && (
                          <>
                            {'\n'}
                            <strong>Context:</strong> {JSON.stringify(error.context, null, 2)}
                          </>
                        )}
                        {error.stack && (
                          <>
                            {'\n'}
                            <strong>Stack Trace:</strong>
                            {'\n'}
                            {error.stack}
                          </>
                        )}
                        {errorInfo && (
                          <>
                            {'\n'}
                            <strong>Component Stack:</strong>
                            {'\n'}
                            {errorInfo.componentStack}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Collapse>
                </>
              )}
            </CardContent>

            <CardActions>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
            </CardActions>
          </Card>
        </Box>
      );
    }

    return children;
  }

  private getSeverityLevel(severity: ErrorSeverity): 'error' | 'warning' | 'info' {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        return 'error';
      case ErrorSeverity.MEDIUM:
        return 'warning';
      case ErrorSeverity.LOW:
      default:
        return 'info';
    }
  }
}

// Higher-order component for easier usage
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WithErrorBoundaryComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithErrorBoundaryComponent;
}

// Hook for functional components to trigger error boundary
export function useErrorHandler(): (error: Error | string) => void {
  return (error: Error | string) => {
    const errorObj = error instanceof Error ? error : new Error(error);
    throw errorObj;
  };
}

export default ErrorBoundary;